import { ExternalLink } from "lucide-react";
import type { OrderDto } from "@/shared/api/types";
import { Alert } from "@/shared/ui/alert";
import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Icon } from "@/shared/ui/icon";
import { formatOrgDate } from "@/shared/utils/dates";
import { orderKindLabel } from "@/shared/utils/labels";
import { formatMoney, formatPercentBp } from "@/shared/utils/numbers";
import { resolvePayUrl } from "@/shared/utils/orders";

interface OrderDialogProps {
  order: OrderDto | null;
  onOpenChange: (open: boolean) => void;
  tz: string;
  /**
   * Fake mode puts Pay and Expire on the api's own page, reached through the portal's own origin so the
   * session cookie travels (`resolvePayUrl`); live mode opens the gateway's hosted page verbatim.
   */
  mode: "razorpay" | "fake";
  /** Called when the hosted page is opened: the caller starts watching for the webhook to land. */
  onPaying: () => void;
  /**
   * Set while the invoice in the dialog is being replaced (ADR-0015 addendum of 2026-10-04): the renewal
   * cost something else after it was raised, and the worker is raising the right one. `totalPaise` is what
   * the renewal costs now. Pay is withheld for as long as it is set — paying the old invoice would charge
   * the wrong amount — and the caller clears it when the window for the replacement is over, so a customer
   * is never left unable to pay. Callers that never raise a renewal leave it out.
   */
  replacing?: { totalPaise: number } | null;
}

/**
 * What an order is for and what it costs, before the customer leaves for the hosted page (ADR-0015 §3.2).
 * The lines are the api's, including any discount by name and the days a prorated line covers, so this
 * dialog and the invoice cannot disagree. A discounted line shows the list price struck through beside the
 * charged one, and the totals run list price → discount → subtotal: `order.subtotalPaise` is already AFTER
 * the discount, so printing "Subtotal" and then "Discount" read as taking it off twice.
 *
 * The page opens in a new tab rather than replacing the portal: the payment is finished on the gateway's
 * own site, and a customer who comes back to a blank portal thinks something broke. Nothing here decides
 * that the order is paid — `onPaying` starts the polling window and the webhook does the rest.
 */
export function OrderDialog({ order, onOpenChange, tz, mode, onPaying, replacing = null }: OrderDialogProps) {
  const payable = order?.status === "issued" || order?.status === "draft";
  return (
    <Dialog open={order !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        {order ? (
          <>
            <DialogHeader>
              <DialogTitle>
                {orderKindLabel(order.kind)} — {formatMoney(order.totalPaise)}
              </DialogTitle>
              <DialogDescription>
                {order.dueAt
                  ? `Due ${formatOrgDate(order.dueAt, tz)}. Nothing changes until it is paid.`
                  : "Nothing changes until it is paid."}
              </DialogDescription>
            </DialogHeader>
            <dl className="flex flex-col gap-1.5 rounded-md border border-border bg-bg p-3 text-cell">
              {order.lines.map((line) => (
                <div key={`${line.itemCode}-${line.quantity}`} className="flex justify-between gap-4">
                  <dt className="min-w-0 text-fg-muted">
                    {line.name}
                    <span className="text-fg-faint">
                      {" "}
                      · {line.quantity} ×{" "}
                      {line.discount ? (
                        <>
                          <span className="line-through">{formatMoney(line.listUnitPaise)}</span>{" "}
                        </>
                      ) : null}
                      {formatMoney(line.unitPaise)}
                    </span>
                    {line.days != null && line.periodDays != null ? (
                      <span className="text-fg-faint">
                        {" "}
                        · {line.days} of {line.periodDays} days
                      </span>
                    ) : null}
                    {line.discount ? (
                      <span className="block text-caption text-accent-fg">
                        {line.discount.name} · {formatPercentBp(line.discount.percentBp)}% off · −
                        {formatMoney(line.discount.amountPaise)}
                      </span>
                    ) : null}
                  </dt>
                  <dd className="text-fg" data-numeric>
                    {formatMoney(line.amountPaise)}
                  </dd>
                </div>
              ))}
              {order.discountPaise > 0 ? (
                <>
                  <div className="flex justify-between gap-4 border-t border-border pt-1.5 text-fg-muted">
                    <dt>List price</dt>
                    <dd data-numeric>{formatMoney(order.subtotalPaise + order.discountPaise)}</dd>
                  </div>
                  <div className="flex justify-between gap-4 text-accent-fg">
                    <dt>Discount</dt>
                    <dd data-numeric>−{formatMoney(order.discountPaise)}</dd>
                  </div>
                  <div className="flex justify-between gap-4 text-fg-muted">
                    <dt>Subtotal</dt>
                    <dd data-numeric>{formatMoney(order.subtotalPaise)}</dd>
                  </div>
                </>
              ) : (
                <div className="flex justify-between gap-4 border-t border-border pt-1.5 text-fg-muted">
                  <dt>Subtotal</dt>
                  <dd data-numeric>{formatMoney(order.subtotalPaise)}</dd>
                </div>
              )}
              <div className="flex justify-between gap-4 text-fg-muted">
                <dt>GST</dt>
                <dd data-numeric>{formatMoney(order.taxPaise)}</dd>
              </div>
              <div className="flex justify-between gap-4 font-medium text-fg">
                <dt>Total</dt>
                <dd data-numeric>{formatMoney(order.totalPaise)}</dd>
              </div>
            </dl>
            {mode === "fake" ? (
              <Alert tone="info" title="This stack is in test mode">
                The payment page is our own: it carries Pay and Expire buttons instead of a real gateway.
                Nothing is charged.
              </Alert>
            ) : null}
            {replacing ? (
              <Alert tone="info" title="This invoice is being updated">
                The price of your renewal changed after this invoice was raised — it now costs{" "}
                {formatMoney(replacing.totalPaise)}. A fresh invoice replaces this one in a few seconds.
              </Alert>
            ) : null}
            {!payable ? (
              <Alert tone="info" title="This order cannot be paid">
                It is already {order.status.replace("_", " ")}. Ask Nexborg for a fresh invoice if you still
                owe something.
              </Alert>
            ) : null}
            <DialogFooter>
              <Button variant="secondary" onClick={() => onOpenChange(false)}>
                Later
              </Button>
              {payable && order.payUrl && !replacing ? (
                <Button
                  asChild
                  variant="primary"
                  onClick={() => {
                    onPaying();
                    onOpenChange(false);
                  }}
                >
                  <a href={resolvePayUrl(order.payUrl, mode)} target="_blank" rel="noreferrer">
                    <Icon icon={ExternalLink} size={16} />
                    Pay {formatMoney(order.totalPaise)} now
                  </a>
                </Button>
              ) : null}
            </DialogFooter>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
